import React, {useState, useEffect, useRef} from 'react';
import Link from 'next/link';
import axios from 'axios';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import styles from '../styles/lead/Lead.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faLightbulb, faCoins, faChevronLeft} from '@fortawesome/free-solid-svg-icons';
import LoanInfo from '../compare/loans/LoanInfo';
import LoanDetails from '../compare/loans/LoanDetails';
import CreditCardDetails from '../compare/list/CreditCardDetails';
import CreditCardInfo from '../compare/list/CreditCardInfo';
import LeasingInfo from '../compare/list/LeasingInfo';
import LeasingDetails from '../compare/list/LeasingDetails';
import PaydayLoanInfo from '../compare/list/PaydayLoanInfo';
import PaydayLoanDetails from '../compare/list/PaydayLoanDetails';
import {getLeadQueryString} from '../utils/general/LeadUtilities';

const SimilarOffers = (props) => {
    const {leadId} = props;

    const [offers, editOffers] = useState([]);
    const [filters, editFilters] = useState(false);
    const [summary, editSummary] = useState(false);
    const [showDetails, editShowDetails] = useState(false);
    const [selectedProduct, editSelectedProduct] = useState(false);
    const isMounted = useRef(null);

    const applyParams = filters ? 
                    getLeadQueryString(
                        filters.product, 
                        filters.hasOwnProperty('type') ? filters.type : false, 
                        filters.hasOwnProperty('amount') ? filters.amount : false, 
                        filters.hasOwnProperty('currency') ? filters.currency : false, 
                        filters.hasOwnProperty('term') ? filters.term : false) 
                    : false;

    useEffect(()=>{
        isMounted.current = true;
        if (leadId>0) {
            getOffers();
        }
        return ()=>{
            isMounted.current = false;
        }   
        }, []
    );

    const viewDetails = (id) => {
        editShowDetails(true);
        let selected = offers.filter(o => o.id==id);
        if (selected) {
            editSelectedProduct(selected[0]);
        }
    }

    const closeDetails = () => { editShowDetails(false)};
    
    const getOffers = async () => {
        try {
            let url = process.env.NEXT_PUBLIC_API + `/api/lead/user/${leadId}/offers`;
            let response = await axios({
                method: 'get',
                url,
                headers: {'Content-Type': 'application/json'},
            });

            const data = response.data;
            if (data.status) {
                if (isMounted.current) {
                    editOffers(data.offers);
                    editFilters(data.filters);
                    editSummary(data.summary);
                }
            }
        } catch(err) {

        }
    }

    const getOfferList = (product) => {
        let html;
        switch(product) {
            case 'creditCard':
                html = offers.map(item =>
                    <CreditCardInfo 
                        key={item.id} 
                        details={item} 
                        viewDetails={viewDetails}
                        applyParams={applyParams}
                    />
                );
                break;
            case 'leasing':
                html = offers.map(item =>
                    <LeasingInfo 
                        key={item.id} 
                        details={item} 
                        viewDetails={viewDetails}
                        applyParams={applyParams}
                    />
                );
                break;
            case 'paydayLoan':
                html = offers.map(item =>
                    <PaydayLoanInfo 
                        key={item.id} 
                        details={item} 
                        viewDetails={viewDetails}
                        applyParams={applyParams}
                    />
                );
                break;
            default:
                html = offers.map(item =>
                    <LoanInfo 
                        key={item.id} 
                        details={item} 
                        currency={filters.currency}
                        amount={filters.amount}
                        term={filters.term/12}
                        viewDetails={viewDetails}
                        applyParams={applyParams}
                    />
                );
                break;
        }
        return html;
    }

    const getDetailsView = (product) => {
        let html;
        switch(product) {
            case 'creditCard':
                    html = 
                    <CreditCardDetails 
                        details={selectedProduct}
                        show={showDetails}
                        handleClose={closeDetails}
                        applyParams={applyParams}
                    />;
                break;
            case 'leasing':
                    html = 
                    <LeasingDetails 
                        details={selectedProduct}
                        show={showDetails}
                        handleClose={closeDetails}
                        applyParams={applyParams}
                    />;
                break;
            case 'paydayLoan':
                    html = 
                    <PaydayLoanDetails 
                        details={selectedProduct}
                        show={showDetails}
                        handleClose={closeDetails}
                        applyParams={applyParams}
                    />;
                break;
            default:
                html = <LoanDetails 
                    details={selectedProduct}
                    show={showDetails}
                    handleClose={closeDetails}
                    currency={filters.currency}
                    amount={filters.amount}
                    term={filters.term}
                    applyParams={applyParams}
                />;
                break;
        }
        return html;
    }
   
    const offerList = getOfferList(filters.product);
    const detailsPanel = showDetails && getDetailsView(filters.product);

    const getBackLink = (product) => {
        let url = '/compara';
        switch(product) {
            case 'personalLoans':
                url += '/credite-nevoi-personale';
                break;
            case 'autoLoans':
                url += '/credite-auto';
                break;
            case 'mortgageLoans':
                url += '/credite-imobiliare';
                break;
            case 'firstHomeLoans':
                url += '/credite-noua-casa';
                break;
            case 'refinanceLoans':
                url += '/refinantare-credit';
                break;
            case 'creditCard':
                url += '/carduri';
                break;
            case 'leasing':
                url += '/leasing-auto';
                break;
            case 'paydayLoan':
                url += '/credite-rapide';
                break;
        }
        return url;
    }

    const backLink = process.env.NEXT_PUBLIC_WEB + getBackLink(filters.product);

    return (
        <>
            <section className={`${styles.infoBox} p-3 my-3 bg-green`} >
                <p className={`${styles.title} font-weight-bold`}>
                    <FontAwesomeIcon icon={faLightbulb} className="me-2 green" heihgt="16" width="16" height="16" width="16" />
                    Cum să-ți crești șansele de obținere a creditului?
                </p>
                <p className={styles.description}>
                    Băncile au criterii diferite de selecție a clienților. 
                    Pentru a-ți spori șansele, solicită oferte de la mai multe bănci. 
                    Aplicația este simplificată, formularul fiind precompletat cu datele personale deja introduse.
                </p>
            </section>
            { (filters && offers.length>0) && 
                <div>
                    <p className="heading2 mb-0 mt-4 text-uppercase">
                        Alte oferte de credit 
                    </p>
                    {filters.product != 'creditCard' &&
                    <p className="mb-0">
                        <FontAwesomeIcon icon={faCoins} className="me-2 red" heihgt="16" width="16" height="16" width="16" />
                        {summary}
                    </p>
                    }
                    <Badge variant="success" >Aplicație simplificată</Badge>
                </div>
            }
            {offerList}
            {filters &&
                <Button as={Link} href={backLink} variant="outline-primary" className="mt-3 mb-5">
                        <FontAwesomeIcon icon={faChevronLeft} height="16" width="16" className="me-2"/>
                        Înapoi la comparații
                    </Button>
            }
            {detailsPanel}
        </>
    );
}

export default SimilarOffers;